import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Divider,
  MenuItem,
  Select,
  TextField,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { usePortfolioRebalanceStore } from "../../Store/portfolioRebalanceStore";
import { useAuth } from "../../Context/AuthProvider"; // Add useAuth import
import InformativeModal from "./InformativeModal";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';
import { getMaxAllowedValueForRebalance } from "./rebalanceMaxValueUtils";
import InputBase from '@mui/material/InputBase';
import Autocomplete from '@mui/material/Autocomplete';
import { tokens, withAlpha } from "../../Common/designTokens";
import {
  createRebalanceRequestId,
  waitForRebalanceResult,
  clearRebalanceResultWaiter,
  JOB_STATUS,
  REBALANCE_RESULT_WINDOW_MS,
} from "../../Utils/portfolioRebalanceJobStorage";
import { useOpenRebalanceResult } from "./PortfolioRebalanceJobWatcher";

// The workbench loader waits this long for the async result before handing off
// to the background notification ("taking longer than usual..."). Shared with the
// watcher (which uses the same window to know a result is "fast") so they agree.
const REBALANCE_LOADER_TIMEOUT_MS = REBALANCE_RESULT_WINDOW_MS;

// --- Validation logic separated out ---
export const validateRebalanceInputs = ({
  portfolioSummary,
  selectedBondsTypeCount,
}) => {
  // Only require targetValue > 0 for non-rating types
  const isRatingType =
    portfolioSummary.rebalance === "S&P Rating" ||
    portfolioSummary.rebalance === "Moody's Rating";

  const hasTargetValue =
    portfolioSummary.targetValue &&
    portfolioSummary.targetValue.trim() !== "" &&
    (isRatingType || Number(portfolioSummary.targetValue) > 0); // Only check > 0 if not rating

  const hasSelectedBonds = selectedBondsTypeCount.total >= 1;
  const hasRebalanceType =
    portfolioSummary.rebalance && portfolioSummary.rebalance.trim() !== "";

  return hasTargetValue && hasSelectedBonds && hasRebalanceType;
};

// --- Input restrictions modularised ---
const getInputRestrictions = (rebalanceType) => {
  switch (rebalanceType) {
    case "Duration":
    case "Yield":
      return { type: "text", inputProps: {}, pattern: /^[0-9]*\.?[0-9]{0,2}$/ }; // Use same as Yield
    case "DV01":
      return { type: "text", inputProps: { min: 0, max: 100000, step: 0.01 }, pattern: /^[0-9]*\.?[0-9]{0,2}$/ };
    case "S&P Rating":
    case "Moody's Rating":
      return { type: "text", inputProps: {}, pattern: null };
    default:
      return { type: "text", inputProps: {}, pattern: /^[0-9]*\.?[0-9]*$/ };
  }
};

// --- Default values modularised ---
const getDefaultValues = (portfolioSummary) => {
  let minTradeQty = portfolioSummary.minTradeQty;
  let toleranceValue = portfolioSummary.toleranceValue;
  let rebalanceType = portfolioSummary.rebalance;
  // New: lotSize defaulting
  let lotSize = portfolioSummary.lotSize;

  if (!minTradeQty || minTradeQty.trim() === "") {
    minTradeQty = "0";
  }
  if (!toleranceValue || toleranceValue.trim() === "") {
    if (rebalanceType === "Yield" || rebalanceType === "Duration" || rebalanceType === "S&P Rating" || rebalanceType === "Moody's Rating") {
      toleranceValue = "0.5"; // Use same default as Yield
    } else if (rebalanceType === "DV01") {
      toleranceValue = "50";
    } else {
      toleranceValue = "0.5";
    }
  }

  // Only provide a default lotSize value here; caller may choose when to apply it.
  if (!lotSize || (typeof lotSize === 'string' && lotSize.trim() === '')) {
    lotSize = "1000";
  }

  return { minTradeQty, toleranceValue, lotSize };
};

const PortfolioRebalanceHeader = () => {
  const navigate = useNavigate();
  const { userId } = useAuth(); // Get userId from useAuth
  const {
    portfolioSummary,
    positions,
    rebalance,
    accounts,
    selectedAccount,
    setSelectedAccount,
    accountSummary,
    fetchAccounts,
    fetchPMUsers,
    pmUsers,
    selectedBondsTypeCount,
    rebalanceApiLoading,
    rebalanceApiError,
    rebalanceJobs,
    cancelRebalance,
    setRebalance,
    setTargetValue,
    setMinTradeQty,
    setToleranceValue,
    setResidual,
    snpRatingOptions,
    moodyRatingOptions,
    fetchSnpRatingOptions,
    fetchMoodyRatingOptions,
    setTargetRating,
    fetchAccountPositions,
    setSelectedBondType,
    selectedPositionsData,
    allPositionsForValidation,
    // add lot size setter
    setLotSize,
    virtualZeroPosition,
    clearPositionSelection,
    setRebalanceLoaderActive,
  } = usePortfolioRebalanceStore();
  const [showRebalanceLoader, setshowRebalanceLoader] = useState(false);
  // Mirror the loader into the store as `rebalanceLoaderActive`. NOT CURRENTLY
  // CONSUMED — the watcher used to disable its snackbar while this was true
  // (superseded by cancel-and-view, see PortfolioRebalanceJobWatcher.jsx); kept
  // written in case that approach is worth reviving. Reset on unmount so it
  // never gets stuck true after navigating away.
  useEffect(() => {
    setRebalanceLoaderActive(showRebalanceLoader);
  }, [showRebalanceLoader, setRebalanceLoaderActive]);
  useEffect(() => () => setRebalanceLoaderActive(false), [setRebalanceLoaderActive]);
  // The account + requestId this run's loader is waiting on (set once the ACK
  // is accepted and we start awaiting the async result — see handleRebalance).
  const pendingRequestRef = useRef(null);
  // If our in-flight job gets cancelled elsewhere while we're waiting (e.g.
  // the notification's "View Results" cancels it so the user can view a
  // different, already-finished account instead — see
  // PortfolioRebalanceJobWatcher's handleView), resolveRebalanceResultWaiter
  // and the 60s timeout will never fire for it, so the loader would otherwise
  // spin forever. Notice the tracked job vanishing/changing and drop it here.
  useEffect(() => {
    if (!showRebalanceLoader) return;
    const pending = pendingRequestRef.current;
    if (!pending) return;
    const currentJob = rebalanceJobs[String(pending.accountId)];
    if (!currentJob || currentJob.requestId !== pending.requestId) {
      setshowRebalanceLoader(false);
      pendingRequestRef.current = null;
    }
  }, [showRebalanceLoader, rebalanceJobs]);
  // Confirm-cancel popup for an in-progress rebalance.
  const [showCancelModal, setShowCancelModal] = useState(false);
  // Presents a READY async result (Order Preview for trades, modal otherwise).
  const openRebalanceResult = useOpenRebalanceResult();
  // The async job for the CURRENTLY-SELECTED account (rebalancing is account-level).
  const selectedAccountJob =
    selectedAccount != null ? rebalanceJobs[String(selectedAccount)] : null;
  // A delivered result the user hasn't opened yet (for the selected account).
  const isRebalanceResultReady =
    selectedAccountJob?.status === JOB_STATUS.READY && !selectedAccountJob.seen;
  // The selected account is still computing.
  const isRebalancePending = selectedAccountJob?.status === JOB_STATUS.PENDING;
  // This account's Rebalance stays disabled while it is pending OR its result is
  // ready-but-unviewed. Other accounts are unaffected.
  const hasActiveRebalanceJob =
    !!selectedAccountJob &&
    (selectedAccountJob.status === JOB_STATUS.PENDING || isRebalanceResultReady);
  // Add state for error/info modal
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [infoModalMessage, setInfoModalMessage] = useState("");
  // Track if the modal is for a 400 error
  const [isErrorModal, setIsErrorModal] = useState(false);
  // Prepare account options for Autocomplete
  const accountOptions = accounts.map((account) => ({
    value: account.id,
    label: account.accountIdentifier,
    description: account.description ? account.description : "---",
    userName: account.userName,
    accountNumber: account.accountNumber,
    custodianName: account.custodianName,
    accountIdentifier: account.accountIdentifier,
  }));

  // Safety net for the full-screen loader: if a result for the selected account
  // becomes READY while the loader is still up, present it and drop the loader —
  // no matter HOW the result arrived (this tab's WS/poll, or another tab writing
  // it to the shared job map, seen here via the storage-sync). The imperative
  // handleRebalance() flow already handles the common single-tab case; this
  // guarantees the loader can never get stuck spinning behind a ready result
  // (the "loader stuck + View Results on top" bug). openRebalanceResult is a
  // no-op if the job was already opened/cleared, so a double-fire is harmless.
  useEffect(() => {
    if (showRebalanceLoader && isRebalanceResultReady) {
      setshowRebalanceLoader(false);
      openRebalanceResult(selectedAccount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showRebalanceLoader, isRebalanceResultReady, selectedAccount]);

  const handleRebalance = async () => {
    setshowRebalanceLoader(true);
    pendingRequestRef.current = null;
    const { fetchAccountPositions, selectedAccount } = usePortfolioRebalanceStore.getState();
    const token = localStorage.getItem("token");
    if (selectedAccount && token) {
      setSelectedBondType("All");
      await fetchAccountPositions(selectedAccount, token, 0, 1000, "updatedAt", "desc", "All");
    }

    // Ensure default values are set if somehow they're empty
    const { portfolioSummary, setMinTradeQty, setToleranceValue, setLotSize } = usePortfolioRebalanceStore.getState();
    const defaults = getDefaultValues(portfolioSummary);
    if (!portfolioSummary.minTradeQty || portfolioSummary.minTradeQty.trim() === "") {
      setMinTradeQty(defaults.minTradeQty);
    }
    if (!portfolioSummary.toleranceValue || portfolioSummary.toleranceValue.trim() === "") {
      setToleranceValue(defaults.toleranceValue);
    }
    // Apply lotSize default only at the moment Rebalance is clicked (user may clear the field earlier)
    if (!portfolioSummary.lotSize || (typeof portfolioSummary.lotSize === 'string' && portfolioSummary.lotSize.trim() === '')) {
      setLotSize(defaults.lotSize);
    }

    // Async rebalance: generate the requestId up front and register the result
    // waiter BEFORE the request, so a fast WebSocket result is never missed. The
    // loader stays for up to 60s; if the result lands we present it immediately,
    // otherwise we tell the user they'll be notified and hand off to the watcher.
    const requestId = createRebalanceRequestId();
    const resultPromise = waitForRebalanceResult(requestId, REBALANCE_LOADER_TIMEOUT_MS);

    let ack;
    try {
      ack = await rebalance(requestId);
    } catch (e) {
      clearRebalanceResultWaiter(requestId);
      setshowRebalanceLoader(false);
      setInfoModalMessage("Something went wrong. Please try again.");
      setIsErrorModal(true);
      setShowInfoModal(true);
      return;
    }

    // ACK problems — nothing is in flight, so stop waiting.
    if (!ack || !ack.success) {
      clearRebalanceResultWaiter(requestId);
      setshowRebalanceLoader(false);
      if (ack?.alreadyPending) {
        setInfoModalMessage(ack.message || "A rebalance is already in progress.");
        setIsErrorModal(false);
      } else {
        setInfoModalMessage(ack?.message || "Something went wrong. Please try again.");
        setIsErrorModal(true);
      }
      setShowInfoModal(true);
      return;
    }

    // Reused a cached result (identical inputs) — no async job was started, the
    // preview data is already in the store; go straight to it.
    if (ack.cached) {
      clearRebalanceResultWaiter(requestId);
      setshowRebalanceLoader(false);
      navigate("/portfolio-rebalance/order-preview");
      return;
    }

    // ACK accepted — wait for the async result (<=60s). Track which job this
    // is so the effect above can tell if it gets cancelled out from under us.
    pendingRequestRef.current = { accountId: ack.accountId, requestId };
    const outcome = await resultPromise;
    setshowRebalanceLoader(false);
    pendingRequestRef.current = null;

    if (outcome.timedOut) {
      // The watcher (WS/poll) will surface the result via the notification.
      setInfoModalMessage(
        "The rebalance is taking longer than usual. You'll be notified when it's done."
      );
      setIsErrorModal(false);
      setShowInfoModal(true);
      return;
    }

    // Result arrived within the window — present it for the account it ran on
    // (Order Preview for trades, or the info/error modal on the workbench).
    openRebalanceResult(ack.accountId);
  };

  const buildPortfolioPlayTrades = () => {
    const trades = (positions || []).map((position) => {
      const currentQuantity = Number(position.currentQuantity ?? position.quantity ?? 0);
      const currentPrice = Number(position.currentPrice ?? position.price ?? 0);
      return {
        ...position,
        side: position.side || position.action || "",
        action: position.action || position.side || "",
        currentQuantity,
        currentPrice,
        quantity: currentQuantity,
        tradeQuantity:
          position.tradeQuantity !== undefined && position.tradeQuantity !== null
            ? Number(position.tradeQuantity)
            : 0,
        endingQuantity:
          position.endingQuantity !== undefined && position.endingQuantity !== null
            ? Number(position.endingQuantity)
            : currentQuantity,
        tradeValue:
          position.tradeValue !== undefined && position.tradeValue !== null
            ? Number(position.tradeValue)
            : 0,
        endingSecurityMarketValue:
          position.endingSecurityMarketValue !== undefined &&
            position.endingSecurityMarketValue !== null
            ? Number(position.endingSecurityMarketValue)
            : Number(position.securityMarketValue ?? 0),
        endingSecurityWeightPercentage:
          position.endingSecurityWeightPercentage !== undefined &&
            position.endingSecurityWeightPercentage !== null
            ? Number(position.endingSecurityWeightPercentage)
            : Number(position.securityWeightPercentage ?? 0),
        includeInRebalancing: false, // Portfolio Play always starts with all rows un-included
      };
    });

    // Include virtual zero position if it exists
    if (virtualZeroPosition) {
      const virtualTrade = {
        ...virtualZeroPosition,
        side: virtualZeroPosition.side,
        action: virtualZeroPosition.action,
        currentQuantity: 0,
        currentPrice: Number(virtualZeroPosition.price),
        quantity: 0,
        tradeQuantity: 0,
        endingQuantity: 0,
        tradeValue: 0,
        endingSecurityMarketValue: 0,
        endingSecurityWeightPercentage: 0,
        includeInRebalancing: false, // Portfolio Play always starts with all rows un-included
      };
      trades.push(virtualTrade);
    }

    return trades;
  };

  const handlePortfolioPlay = () => {
    // Ensure default values are set for Portfolio Play if user left fields empty
    const {
      portfolioSummary: latestPortfolioSummary,
      setMinTradeQty,
      setToleranceValue,
      setLotSize,
    } = usePortfolioRebalanceStore.getState();
    const defaults = getDefaultValues(latestPortfolioSummary);

    if (
      !latestPortfolioSummary.minTradeQty ||
      latestPortfolioSummary.minTradeQty.trim() === ""
    ) {
      setMinTradeQty(defaults.minTradeQty);
    }
    if (
      !latestPortfolioSummary.toleranceValue ||
      latestPortfolioSummary.toleranceValue.trim() === ""
    ) {
      setToleranceValue(defaults.toleranceValue);
    }
    if (
      !latestPortfolioSummary.lotSize ||
      (typeof latestPortfolioSummary.lotSize === "string" &&
        latestPortfolioSummary.lotSize.trim() === "")
    ) {
      setLotSize(defaults.lotSize);
    }

    const initialTrades = buildPortfolioPlayTrades();
    // Clear all checkbox state in the store so pressing Back shows no ticked rows
    clearPositionSelection();
    navigate("/portfolio-rebalance/portfolio-play", {
      state: {
        initialTrades,
        mode: "portfolio-play",
        rebalanceType: portfolioSummary?.rebalance || "",
      },
    });
  };

  const token = localStorage.getItem("token");

  // Notify parent about initial loading state
  useEffect(() => {
    // default to first account once accounts load
    if (accounts.length > 0 && !selectedAccount) {
      setSelectedAccount(accounts[0].id);
    }
  }, [accounts, selectedAccount, setSelectedAccount]);

  const formatCurrency = (value) => {
    if (typeof value === "number") {
      let formatted = value.toLocaleString("en-US", {
        minimumFractionDigits: 5,
        maximumFractionDigits: 5
      }).replace(/(\.\d*?[1-9])0+$/, "$1").replace(/\.0+$/, "");
      return `$${formatted}`;
    }
    return value || "---";
  };

  const formatPercentage = (value) => {
    if (typeof value === "number") {
      let formatted = value.toLocaleString("en-US", {
        minimumFractionDigits: 5,
        maximumFractionDigits: 5
      }).replace(/(\.\d*?[1-9])0+$/, "$1").replace(/\.0+$/, "");
      return `${formatted}%`;
    }
    return value || "---";
  };

  const formatNumeric = (value) => {
    if (typeof value === "number") {
      let formatted = value.toLocaleString("en-US", {
        minimumFractionDigits: 5,
        maximumFractionDigits: 5
      }).replace(/(\.\d*?[1-9])0+$/, "$1").replace(/\.0+$/, "");
      return formatted;
    }
    return value || "---";
  };

  // Handle account selection change
  const handleAccountChange = (event) => {
    const accountId = event.target.value;
    const accountExists = accounts.some(account => account.id === accountId);

    if (accountExists) {
      setSelectedAccount(accountId);
      setRebalance(""); // Reset rebalance dropdown selection when account changes
    } else {
      console.error("Invalid account selection:", accountId);
    }
  };

  // Handle rebalance selection change
  const handleRebalanceChange = (event) => {
    const rebalanceValue = event.target.value;
    setRebalance(rebalanceValue); // <-- use store setter
    setTargetValue("");           // Reset rebalance value

    // Set default values immediately when rebalance type changes
    const defaults = getDefaultValues({ rebalance: rebalanceValue });
    setMinTradeQty(defaults.minTradeQty);
    setToleranceValue(defaults.toleranceValue);
  };

  // Handle rating dropdown change
  const handleTargetRatingChange = (event) => {
    setTargetValue(event.target.value);
  };

  // Handle account selection change for Autocomplete
  const handleAccountAutocompleteChange = (event, newValue) => {
    if (newValue && newValue.value) {
      setSelectedAccount(newValue.value);
      setRebalance(""); // Reset rebalance dropdown selection when account changes
    }
  };

  // Replace MetricItem definition (remove hardcoded colors)
  const MetricItem = ({ label, value, showSeparator = true }) => (
    <>
      <Divider
        orientation="vertical"
        flexItem
        sx={{
          mr: "4px",
          backgroundColor: tokens.colors.bodyBorderColor50,
          height: "40px",
          alignSelf: "center",
        }}
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          mr: showSeparator ? 2 : 0,
        }}
      >
        <Typography
          variant="body2"
          sx={{ color: tokens.colors.fontColour50, fontSize: "14px", mb: 0.5 }}
        >
          {label}
        </Typography>
        <Typography
          variant="body1"
          sx={{ color: tokens.colors.fontColour, fontWeight: 400, fontSize: "14px" }}
        >
          {value}
        </Typography>
      </Box>
    </>
  );

  // Get max allowed value for current rebalance variable from all positions
  const maxAllowedValue = portfolioSummary.rebalance === "DV01"
    ? 100000 // <-- changed max to 100000
    : getMaxAllowedValueForRebalance(
      portfolioSummary.rebalance,
      allPositionsForValidation, // Use unfiltered positions for validation
      portfolioSummary.rebalance === "S&P Rating"
        ? snpRatingOptions
        : portfolioSummary.rebalance === "Moody's Rating"
          ? moodyRatingOptions
          : undefined
    );

  // Track if target value is above max allowed
  const isTargetValueAboveMax =
    portfolioSummary.rebalance !== "DV01" // Skip check for DV01
    && maxAllowedValue !== null
    && portfolioSummary.targetValue !== ""
    && (
      portfolioSummary.rebalance === "S&P Rating" || portfolioSummary.rebalance === "Moody's Rating"
        ? (() => {
          // For ratings, compare the value of selected target rating to maxAllowedValue
          const options =
            portfolioSummary.rebalance === "S&P Rating"
              ? snpRatingOptions
              : moodyRatingOptions;
          const selectedRatingObj = options.find(opt => opt.rating === portfolioSummary.targetValue);
          return selectedRatingObj && selectedRatingObj.value < maxAllowedValue;
        })()
        : !isNaN(Number(portfolioSummary.targetValue)) &&
        Number(portfolioSummary.targetValue) > maxAllowedValue
    );

  // Get the rating string for display in error tooltip
  const getMaxRatingForDisplay = () => {
    if (portfolioSummary.rebalance === "S&P Rating" || portfolioSummary.rebalance === "Moody's Rating") {
      const options = portfolioSummary.rebalance === "S&P Rating" ? snpRatingOptions : moodyRatingOptions;
      const maxRatingObj = options.find(opt => opt.value === maxAllowedValue);
      return maxRatingObj ? maxRatingObj.rating : maxAllowedValue;
    }
    return maxAllowedValue;
  };

  // Handle field changes (modularised restrictions)
  const handleTargetValueChange = (event) => {
    const value = event.target.value;
    const { pattern } = getInputRestrictions(portfolioSummary.rebalance);

    if (portfolioSummary.rebalance === "DV01") {
      // Special handling for DV01 to enforce max limit
      if (value === "" || (pattern.test(value) && Number(value) <= 100000)) { // <-- changed max to 100000
        setTargetValue(value);
      }
    } else if (portfolioSummary.rebalance === "Duration" || portfolioSummary.rebalance === "Yield") {
      // Use same logic for Duration and Yield
      if (
        value === "" ||
        (pattern.test(value) && Number(value) >= 0)
      ) {
        setTargetValue(value);
      }
    } else if (value === "" || pattern.test(value)) {
      setTargetValue(value);
    }
  };

  const handleMinTradeQtyChange = (event) => {
    const value = event.target.value;
    if (value === "" || (/^\d+$/.test(value) && Number(value) <= 10000000)) {
      setMinTradeQty(value);
    }
  };

  const handleToleranceValueChange = (event) => {
    const value = event.target.value;
    const { pattern } = getInputRestrictions(portfolioSummary.rebalance);
    if (portfolioSummary.rebalance === "DV01") {
      // Allow floats up to two decimals, min 0, max 100
      if (
        value === "" ||
        (pattern.test(value) && Number(value) >= 0 && Number(value) <= 100)
      ) {
        setToleranceValue(value);
      }
    } else if (portfolioSummary.rebalance === "Duration" || portfolioSummary.rebalance === "Yield") {
      // Use same logic for Duration and Yield
      if (
        value === "" ||
        (pattern.test(value) && Number(value) >= 0)
      ) {
        setToleranceValue(value);
      }
    } else {
      if (value === "" || pattern.test(value)) {
        setToleranceValue(value);
      }
    }
  };

  // Handle lot size change with validation (min 1, max 10000000)
  const handleLotSizeChange = (event) => {
    const value = event.target.value;
    if (value === "" || (/^\d+$/.test(value) && Number(value) >= 1 && Number(value) <= 10000000)) {
      // Allow user to clear the field (set to empty string). Do not coerce to a default here.
      setLotSize(value);
    }
  };

  // Enhanced control field component with editable support
  const ControlField = ({
    label,
    value,
    width = "60px",
    isSelect = false,
    isEditable = false,
    options = [],
    placeholder = "Select...",
    onChange,
    inputType = "text",
    allowEmpty = true,      // allow or hide empty option
  }) => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: 1,
      }}
    >
      {label && (
        <Typography
          variant="body2"
          sx={{
            color: tokens.colors.fontColour50,
            fontSize: "14px",
            fontWeight: "normal",
          }}
        >
          {label}
        </Typography>
      )}
      {isSelect ? (
        <Select
          displayEmpty={allowEmpty}
          value={value || ""}
          onChange={onChange}
          sx={{
            width: "auto",
            minWidth: width,
            height: "30px",
            borderRadius: "8px",
            border: `1px solid ${tokens.colors.bodyBorderColor50}`,
            color: tokens.colors.fontColour8,
            fontSize: "14px",
            fontWeight: "normal",
            backgroundColor: tokens.colors.white,
            "& .MuiSelect-select": { padding: "8px 16px", paddingRight: "36px !important" },
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "transparent" },
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "transparent" },
          }}
          MenuProps={{ PaperProps: { sx: { maxHeight: "300px" } } }}
          renderValue={(selected) => {
            if (!selected)
              return <span style={{ color: tokens.colors.fontColour50 }}>{placeholder}</span>;
            // For account dropdown, show account identifier
            if (onChange === handleAccountChange) {
              const account = accounts.find((acc) => acc.id === selected);
              return account ? account.accountIdentifier : selected;
            }
            return selected;
          }}
        >
          {allowEmpty && (
            <MenuItem value="">
              <span style={{ color: tokens.colors.fontColour50 }}>{placeholder}</span>
            </MenuItem>
          )}
          {options.map((option) => (
            <MenuItem
              key={option.value || option}
              value={option.value || option}
              disabled={!!option.disabled}
              sx={
                option.disabled
                  ? {
                    color: tokens.colors.disabledFontColour,
                    pointerEvents: "none",
                  }
                  : {}
              }
            >
              {option.label || option}

            </MenuItem>
          ))}
        </Select>
      ) : isEditable ? (
        <TextField
          value={value || ""}
          onChange={onChange}
          type={inputType}
          variant="outlined"
          size="small"
          sx={{
            width,
            "& .MuiOutlinedInput-root": {
              height: "30px",
              borderRadius: "8px",
              border: `1px solid ${tokens.colors.bodyBorderColor50}`,
              "& fieldset": { borderColor: "transparent" },
              "&:hover fieldset": { borderColor: "transparent" },
              "&.Mui-focused fieldset": {
                borderColor: tokens.palette.primaryA25,
                borderWidth: "1px",
              },
              backgroundColor: tokens.colors.white,
            },
            "& .MuiOutlinedInput-input": {
              color: tokens.colors.fontColour8,
              fontSize: "14px",
              padding: "8px 16px",
              "&::placeholder": {
                color: tokens.colors.fontColour50,
                opacity: 1,
              },
            },
            // Hide spinner arrows for number inputs
            "& input[type=number]": {
              MozAppearance: "textfield",
            },
            "& input[type=number]::-webkit-outer-spin-button": {
              WebkitAppearance: "none",
              margin: 0,
            },
            "& input[type=number]::-webkit-inner-spin-button": {
              WebkitAppearance: "none",
              margin: 0,
            },
          }}
          placeholder=""
        />
      ) : (
        <Box
          sx={{
            width,
            height: "30px",
            borderRadius: "8px",
            border: `1px solid ${tokens.colors.bodyBorderColor50}`,
            color: tokens.colors.fontColour8,
            fontSize: "14px",
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: tokens.colors.white
          }}
        >
          {value}
        </Box>
      )}
    </Box>
  );

  // Portfolio metrics data - now using real data from account summary
  const portfolioMetrics = [
    {
      label: "Total Par Value",
      value: formatCurrency(accountSummary.totalPar),
    },
    {
      label: "Total Current Value",
      value: formatCurrency(accountSummary.totalCurrValue),
    },
    {
      label: "Avg Yield to Worst",
      value: formatPercentage(accountSummary.averageYieldToWorst),
    },
    {
      label: "Avg Yield to Maturity",
      value: formatPercentage(accountSummary.avgYieldToMaturity),
    },
    {
      label: "Avg $ Duration",
      value: formatNumeric(accountSummary.average),
    },
    { label: "Avg S&P Rating", value: accountSummary.averageSPRating || "---" },
    {
      label: "Avg Moody's Rating",
      value: accountSummary.averageMoodys || "---",
    },
    {
      label: "Total DV01",
      value: formatNumeric(accountSummary.totalDv01),
    },
    {
      label: "Total Non Taxable",
      value: formatCurrency(accountSummary.nonTaxable),
    },
    { label: "Convexity", value: formatNumeric(accountSummary.convexity) },
    {
      label: "Total Taxable",
      value: formatCurrency(accountSummary.taxable),
      showSeparator: false,
    },
  ];


  const selectedAccountObj = accounts.find(acc => acc.id === selectedAccount) || {};

  // Utility to check if a value is missing (null, undefined, empty, or "N/A")
  const isMissing = (val) =>
    val === null ||
    val === undefined ||
    val === "" ||
    (typeof val === "string" && val.trim().toUpperCase() === "N/A");

  // Map rebalance variable to its base value in accountSummary
  const getBaseValueForRebalance = (rebalanceType, accountSummary) => {
    switch (rebalanceType) {
      case "Duration":
        return accountSummary.average;
      case "Yield":
        return accountSummary.averageYieldToWorst;
      case "S&P Rating":
        return accountSummary.averageSPRating;
      case "Moody's Rating":
        return accountSummary.averageMoodys;
      case "DV01":
        return accountSummary.totalDv01;
      default:
        return null;
    }
  };

  // Prepare rebalance options with disabled state
  const rebalanceOptions = [
    { value: "Duration", label: "$ Duration" },
    { value: "Yield", label: "Yield" },
    { value: "S&P Rating", label: "S&P Rating" },
    { value: "Moody's Rating", label: "Moody's Rating" },
    { value: "DV01", label: "DV01" },
  ].map(opt => ({
    ...opt,
    disabled: isMissing(getBaseValueForRebalance(opt.value, accountSummary)),
  }));
  const getSummaryOrAccount = (summaryValue, accountValue) => {
    if (summaryValue === null || summaryValue === undefined || summaryValue === '') {
      return accountValue || "---";
    }
    return summaryValue;
  };

  // If selected rebalance is not allowed, reset it
  useEffect(() => {
    if (
      portfolioSummary.rebalance &&
      rebalanceOptions.find(
        o => o.value === portfolioSummary.rebalance && o.disabled
      )
    ) {
      setRebalance("");
      setTargetValue("");
      setMinTradeQty("");
      setToleranceValue("");
    }
    // eslint-disable-next-line
  }, [accountSummary]);

  // Check if rebalance should be enabled (now using modular validation)
  // Also: disable if selected rebalance is not allowed
  // Also: disable if target value error (e.g., above max allowed)
  const isRebalanceEnabled = () =>
    validateRebalanceInputs({
      portfolioSummary,
      selectedBondsTypeCount,
    }) &&
    !rebalanceOptions.find(
      o => o.value === portfolioSummary.rebalance && o.disabled
    ) &&
    !isTargetValueAboveMax && // <-- disable if target value error
    // Block a new rebalance while one is in flight OR its result is unviewed.
    !hasActiveRebalanceJob;

  const isRebalanceVariableSelected = () =>
    portfolioSummary.rebalance && !rebalanceOptions.find(
      o => o.value === portfolioSummary.rebalance && o.disabled
    );
  // Portfolio Play should only depend on selected bonds, not target/current input fields
  const isPortfolioPlayEnabled = () =>
    selectedBondsTypeCount.total >= 0 && !rebalanceApiLoading;
  // and there shuld be a rebalance variable selected

  // Reusable TextField style for editable numeric fields
  const numericTextFieldSx = {
    width: "100px",
    "& .MuiOutlinedInput-root": {
      height: "30px",
      borderRadius: "8px",
      border: `1px solid ${tokens.colors.bodyBorderColor50}`,
      "& fieldset": { borderColor: "transparent" },
      "&:hover fieldset": { borderColor: "transparent" },
      "&.Mui-focused fieldset": {
        borderColor: tokens.palette.primaryA25,
        borderWidth: "1px",
      },
      backgroundColor: tokens.colors.white,
    },
    "& .MuiOutlinedInput-input": {
      color: tokens.colors.fontColour8,
      fontSize: "14px",
      fontWeight: "normal",
      padding: "8px 16px",
      "&::placeholder": {
        color: tokens.colors.fontColour50,
        opacity: 1,
      },
    },
    "& input[type=number]": {
      MozAppearance: "textfield",
    },
    "& input[type=number]::-webkit-outer-spin-button": {
      WebkitAppearance: "none",
      margin: 0,
    },
    "& input[type=number]::-webkit-inner-spin-button": {
      WebkitAppearance: "none",
      margin: 0,
    },
  };

  return (
    <>
      {/* Loader for recalculate */}
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 2,
          backgroundColor: "rgba(0, 0, 0, 0.7)",
          flexDirection: "column",
          gap: 2,
        }}
        open={showRebalanceLoader}
      >
        <CircularProgress color="inherit" size={60} />
        <Typography variant="h6" sx={{ color: tokens.colors.white }}>
          Rebalancing...
        </Typography>
      </Backdrop>
      {/* Info/Error Modal */}
      <InformativeModal
        open={showInfoModal}
        message={infoModalMessage}
        onClose={() => setShowInfoModal(false)}
        title={isErrorModal ? "Error" : undefined}
      />
      {/* Confirm cancelling the in-progress rebalance for the selected account */}
      <InformativeModal
        open={showCancelModal}
        title="Cancel Rebalance"
        message="Do you really want to cancel this rebalance?"
        showCancel={true}
        onConfirm={() => {
          setShowCancelModal(false);
          cancelRebalance(selectedAccount);
        }}
        onClose={() => setShowCancelModal(false)}
      />
      <Paper
        sx={{
          backgroundColor: tokens.colors.defaultGridRowColor,
          borderRadius: "0px",
          border: `1px solid ${tokens.colors.bodyBorderColor50}`,
          mb: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            pt: 2,
            pl: 2,
            pr: "10px",
          }}
        >
          {/* Replace Select with Autocomplete for account selection */}
          <Autocomplete
            options={accountOptions}
            getOptionLabel={(option) => option.label}
            value={accountOptions.find(opt => opt.value === selectedAccount) || null}
            onChange={handleAccountAutocompleteChange}
            disableClearable={true}
            filterOptions={(options, { inputValue }) =>
              options.filter(opt =>
                (opt.label && opt.label.toLowerCase().includes(inputValue.toLowerCase())) ||
                (opt.description && opt.description.toLowerCase().includes(inputValue.toLowerCase()))
              )
            }
            sx={{
              width: "150px",
              "& .MuiInputBase-root": {
                height: "30px",
                borderRadius: "8px",
                border: `1px solid ${tokens.colors.bodyBorderColor50}`,
                color: tokens.colors.fontColour8,
                fontSize: "14px",
                fontWeight: "normal",
                backgroundColor: tokens.colors.white,
                paddingRight: "24px !important",
              },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "transparent" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "transparent" },
              "& .MuiAutocomplete-endAdornment": { right: "4px" },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search account"
                variant="outlined"
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: "30px",
                    borderRadius: "8px",
                    border: `1px solid ${tokens.colors.bodyBorderColor50}`,
                    backgroundColor: tokens.colors.white,
                    paddingRight: "24px !important",
                  },
                  "& .MuiOutlinedInput-input": {
                    color: tokens.colors.fontColour,
                    fontSize: "14px",
                    paddingRight: "0px !important",
                  },
                  "& .MuiAutocomplete-endAdornment": { right: "4px" },
                }}
              />
            )}
            renderOption={(props, option) => {
              // Per-account job status dot: orange = rebalancing, green = result
              // ready (unviewed). Nothing when idle.
              const job = rebalanceJobs[String(option.value)];
              const dotColor =
                job?.status === JOB_STATUS.PENDING
                  ? tokens.colors.amberColor
                  : job?.status === JOB_STATUS.READY && !job.seen
                  ? tokens.colors.buyFontColor
                  : null;
              return (
                <li {...props}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                      gap: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <Typography variant="body2" sx={{ color: tokens.colors.fontColour, fontWeight: 500 }}>
                        {option.label}
                      </Typography>
                      <Typography variant="caption" sx={{ color: tokens.colors.fontColour50 }}>
                        {option.description}
                      </Typography>
                    </Box>
                    {dotColor && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: dotColor,
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </Box>
                </li>
              );
            }}
          />
          {/* PM / account details text */}
          <Typography
            variant="body2"
            sx={{
              color: tokens.colors.fontColour85,
              fontWeight: 700,
              fontSize: "14px",
              whiteSpace: "pre-line",
            }}
          >
            {/* Name: {getSummaryOrAccount(accountSummary.userName, selectedAccountObj.userName)} &nbsp; | &nbsp; */}
            Account #: {getSummaryOrAccount(accountSummary.accountIdentifier, selectedAccountObj.accountNumber || selectedAccountObj.accountIdentifier)} &nbsp; | &nbsp;
            Account Description: {getSummaryOrAccount(accountSummary.accountDescription, selectedAccountObj.description)} &nbsp; | &nbsp;
            Custodian: {getSummaryOrAccount(accountSummary.custodianName, selectedAccountObj.custodianName)}
          </Typography>

          {/* The selected account is rebalancing (right-aligned). Black text +
              primary spinner + a Cancel link that confirms before dropping it.
              Hidden while the <60s full-screen loader is showing (the loader is
              the in-progress indicator during that window). */}
          {isRebalancePending && !showRebalanceLoader && (
            <Box
              sx={{
                ml: "auto",
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexShrink: 0,
              }}
            >
              <CircularProgress size={14} sx={{ color: tokens.palette.primary }} />
              <Typography
                sx={{ color: tokens.colors.fontColour, fontSize: "14px", fontWeight: 600 }}
              >
                Rebalance in progress
              </Typography>
              <Button
                onClick={() => setShowCancelModal(true)}
                size="small"
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "14px",
                  color: tokens.palette.danger,
                  textDecoration: "underline",
                  minWidth: 0,
                  p: "2px 6px",
                  "&:hover": {
                    textDecoration: "underline",
                    backgroundColor: withAlpha(tokens.palette.danger, 0.08),
                  },
                }}
              >
                Cancel
              </Button>
            </Box>
          )}

          {/* Async result ready — surfaced inline (right-aligned), styled the same
              as the notification snackbar (black text + primary "View Results"). */}
          {isRebalanceResultReady && (
            <Box
              sx={{
                ml: "auto",
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexShrink: 0,
              }}
            >
              <Typography
                sx={{ color: tokens.colors.fontColour, fontSize: "14px", fontWeight: 600 }}
              >
                Rebalancing Complete.
              </Typography>
              <Button
                onClick={() => openRebalanceResult(selectedAccount)}
                size="small"
                sx={{
                  // backgroundColor: tokens.palette.primary,
                  backgroundColor: "transparent",
                  border: `1px solid ${tokens.palette.primary}`,
                  color: tokens.palette.primary ,
                  fontWeight: 500,
                  textTransform: "none",
                  borderRadius:  "8px",
                  fontSize: "14px",
                  px: 1.5,
              "&:hover": { backgroundColor: tokens.palette.primaryA10 },
                }}
              >
                View Results
              </Button> 
            </Box>
          )}
        </Box>

        <Box sx={{ p: 2 }}>
          {/* Main metrics row */}
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                flexWrap: "wrap",
                flex: 1,
                borderRadius: "8px",
                bgcolor: tokens.colors.defaultGridRowColor,
                p: "4px",
                pl: "10px",
                border: `1px solid ${tokens.colors.bodyBorderColor50}`,
              }}
            >
              {portfolioMetrics.map((metric, index) => (
                <MetricItem
                  key={index}
                  label={metric.label}
                  value={metric.value}
                  showSeparator={metric.showSeparator !== false}
                />
              ))}
            </Box>
          </Box>

          {/* Controls and button row */}
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <ControlField
                label={"Rebalance"}
                value={
                  portfolioSummary.rebalance === "Duration" ? "$ Duration" :
                    portfolioSummary.rebalance || ""
                }
                width="90px"
                isSelect={true}
                options={rebalanceOptions}
                placeholder="Select"
                onChange={handleRebalanceChange}
              />

              {portfolioSummary.rebalance && (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: tokens.colors.fontColour80,
                        fontSize: "14px",
                        fontWeight: "normal",
                      }}
                    >
                      Current {portfolioSummary.rebalance === "Duration"
                        ? "$ Duration"
                        : portfolioSummary.rebalance
                      }: {
                        portfolioSummary.rebalance === "Duration"
                          ? accountSummary.average // <-- removed formatYearsToMonths
                          : portfolioSummary.rebalance === "Yield"
                            ? formatPercentage(accountSummary.averageYieldToWorst)
                            : portfolioSummary.rebalance === "DV01"
                              ? formatNumeric(accountSummary.totalDv01)
                              : portfolioSummary.rebalance === "S&P Rating"
                                ? accountSummary.averageSPRating || "---"
                                : portfolioSummary.rebalance === "Moody's Rating"
                                  ? accountSummary.averageMoodys || "---"
                                  : "---"
                      }
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: tokens.colors.fontColour,
                        fontSize: "14px",
                        fontWeight: "normal",
                      }}
                    >
                      to
                    </Typography>
                    {/* Show dropdown for rating types, else TextField */}
                    {portfolioSummary.rebalance === "S&P Rating" ? (
                      <Box sx={{ display: "flex", alignItems: "center", position: "relative" }}>
                        <Select
                          value={portfolioSummary.targetValue || ""}
                          onChange={handleTargetRatingChange}
                          displayEmpty
                          sx={{
                            ...numericTextFieldSx,
                            height: "30px",
                            borderRadius: "8px",
                            border: isTargetValueAboveMax
                              ? `1px solid ${tokens.colors.error}`
                              : `1px solid ${tokens.colors.bodyBorderColor50}`,
                            color: `${tokens.colors.fontColour8}`,
                            fontSize: "14px",
                            fontWeight: "normal",
                            "& .MuiSelect-select": { padding: "8px 16px" },
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "transparent",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "transparent",
                            },
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                maxHeight: "300px",
                              },
                            },
                          }}
                        >
                          <MenuItem value="">
                            <span style={{ color: `${tokens.colors.fontColour50}` }}>Select</span>
                          </MenuItem>
                          {snpRatingOptions.map(opt => (
                            <MenuItem key={opt.rating} value={opt.rating}>
                              {opt.rating}
                            </MenuItem>
                          ))}
                        </Select>
                        {isTargetValueAboveMax && (
                          <Tooltip
                            title={`Target value can't be higher than the maximum (${getMaxRatingForDisplay()}) the bonds in the portfolio`}
                            arrow
                            placement="right"
                          >
                            <InfoOutlinedIcon
                              sx={{
                                color: tokens.colors.error,
                                fontSize: 14,
                                ml: 1,
                                position: "absolute",
                                right: -20,
                                top: "50%",
                                transform: "translateY(-50%)"
                              }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    ) : portfolioSummary.rebalance === "Moody's Rating" ? (
                      <Box sx={{ display: "flex", alignItems: "center", position: "relative" }}>
                        <Select
                          value={portfolioSummary.targetValue || ""}
                          onChange={handleTargetRatingChange}
                          displayEmpty
                          sx={{
                            ...numericTextFieldSx,
                            height: "30px",
                            borderRadius: "8px",
                            border: isTargetValueAboveMax
                              ? `1px solid ${tokens.colors.error}`
                              : `1px solid ${tokens.colors.bodyBorderColor50}`,
                            color: `${tokens.colors.fontColour8}`,
                            fontSize: "14px",
                            fontWeight: "normal",
                            "& .MuiSelect-select": { padding: "8px 16px" },
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "transparent",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "transparent",
                            },
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                maxHeight: "300px",
                              },
                            },
                          }}
                        >
                          <MenuItem value="">
                            <span style={{ color: `${tokens.colors.fontColour80}` }}>Select</span>
                          </MenuItem>
                          {moodyRatingOptions.map(opt => (
                            <MenuItem key={opt.rating} value={opt.rating}>
                              {opt.rating}
                            </MenuItem>
                          ))}
                        </Select>
                        {isTargetValueAboveMax && (
                          <Tooltip
                            title={`Target value can't be higher than the maximum (${getMaxRatingForDisplay()}) the bonds in the portfolio`}
                            arrow
                            placement="right"
                          >
                            <InfoOutlinedIcon
                              sx={{
                                color: tokens.colors.error,
                                fontSize: 14,
                                ml: 1,
                                position: "absolute",
                                right: -20,
                                top: "50%",
                                transform: "translateY(-50%)"
                              }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    ) : (
                      <Box sx={{ display: "flex", alignItems: "center", position: "relative" }}>
                        <TextField
                          value={portfolioSummary.targetValue || ""}
                          onChange={handleTargetValueChange}
                          type={getInputRestrictions(portfolioSummary.rebalance).type}
                          variant="outlined"
                          size="small"
                          sx={{
                            ...numericTextFieldSx,
                            // border: isTargetValueAboveMax
                            //   ? `0.5px solid ${tokens.colors.error}`
                            //   : numericTextFieldSx.border,
                            "& .MuiOutlinedInput-root": {
                              ...(numericTextFieldSx["& .MuiOutlinedInput-root"] || {}),
                              border: isTargetValueAboveMax
                                ? `0.5px solid ${tokens.colors.error}`
                                : `1px solid ${tokens.colors.bodyBorderColor50}`,
                            },
                          }}
                          inputProps={getInputRestrictions(portfolioSummary.rebalance).inputProps}
                        />
                        {isTargetValueAboveMax && (
                          <Tooltip
                            title={`Target value can't be higher than the maximum (${getMaxRatingForDisplay()}) the bonds in the portfolio`}
                            arrow
                            placement="right"
                          >
                            <InfoOutlinedIcon
                              sx={{
                                color: tokens.colors.error,
                                fontSize: 14,
                                ml: 1,
                                position: "absolute",
                                right: -20,
                                top: "50%",
                                transform: "translateY(-50%)"
                              }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    )}
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      pl: 1,
                      color: `${tokens.colors.fontColour80}`,
                      fontSize: "14px",
                      fontWeight: "normal",
                    }}
                  >
                    Tolerance Value:
                  </Typography>
                  {portfolioSummary.rebalance === "S&P Rating" || portfolioSummary.rebalance === "Moody's Rating" ? (
                    <TextField
                      value="0.5"
                      disabled
                      variant="outlined"
                      size="small"
                      sx={{
                        ...numericTextFieldSx,
                        opacity: 0.5,
                        "& .MuiOutlinedInput-root": {
                          height: "30px",
                          borderRadius: "8px",
                          border: `1px solid ${tokens.colors.bodyBorderColor50}`,
                          backgroundColor: tokens.colors.defaultGridRowColor,
                          "&.Mui-focused fieldset": {
                            borderColor: tokens.colors.bodyBorderColor50,
                            borderWidth: "1px",
                          },
                        },
                        "& .MuiOutlinedInput-input": {
                          color: tokens.colors.fontColour80,
                          fontSize: "14px",
                          fontWeight: "normal",
                          padding: "8px 16px",
                          "&::placeholder": {
                            color: `${tokens.colors.fontColour50}`,
                            opacity: 1,
                          },
                        },
                      }}
                    />
                  ) : (
                    <TextField
                      value={portfolioSummary.toleranceValue || ""}
                      onChange={handleToleranceValueChange}
                      type={getInputRestrictions(portfolioSummary.rebalance).type}
                      variant="outlined"
                      size="small"
                      sx={numericTextFieldSx}
                      inputProps={
                        getInputRestrictions(portfolioSummary.rebalance).inputProps
                      }
                    />
                  )}
                  <Typography
                    variant="body2"
                    sx={{
                      pl: 1,
                      color: `${tokens.colors.fontColour80}`,
                      fontSize: "14px",
                      fontWeight: "normal",
                    }}
                  >
                    Min Trade Qty:
                  </Typography>

                  <TextField
                    value={portfolioSummary.minTradeQty || ""}
                    onChange={handleMinTradeQtyChange}
                    type="number"
                    variant="outlined"
                    size="small"
                    sx={numericTextFieldSx}
                  />

                  <Typography
                    variant="body2"
                    sx={{
                      pl: 1,
                      color: `${tokens.colors.fontColour80}`,
                      fontSize: "14px",
                      fontWeight: "normal",
                    }}
                  >
                    Lot Size:
                  </Typography>
                  <TextField
                    value={portfolioSummary.lotSize || ""}
                    onChange={handleLotSizeChange}
                    type="number"
                    variant="outlined"
                    size="small"
                    sx={numericTextFieldSx}
                    inputProps={{ min: 1, max: 10000000 }}
                  />
                  {/* <Typography
                    variant="body2"
                    sx={{
                      color: "rgba(255, 255, 255, 0.5)",
                      fontSize: "14px",
                      fontWeight: "normal",
                    }}
                  >
                  Residual:
                  </Typography>
                  <TextField
                    value={portfolioSummary.residual || ""}
                    onChange={handleResidualChange}
                    type="number"
                    variant="outlined"
                    size="small"
                    sx={numericTextFieldSx}
                  /> */}
                </>
              )}
            </Box>

            <Box sx={{ display: "flex", gap: 1.5 }}>
              <Button
                variant="outlined"
                onClick={handleRebalance}
                disabled={!isRebalanceEnabled()}
                sx={{
                  width: "120px",
                  height: "40px",
                  borderRadius: "8px",
                  backgroundColor: isRebalanceEnabled()
                    ? withAlpha(tokens.colors.buyBgColor, 0.10)
                    : tokens.colors.disabledBackground,
                  border: isRebalanceEnabled()
                    ? `1px solid ${tokens.colors.buyFontColor}`
                    : `1px solid ${tokens.colors.bodyBorderColor50}`,
                  color: isRebalanceEnabled()
                    ? tokens.colors.buyFontColor
                    : tokens.colors.disabledFontColour,
                  fontSize: "14px",
                  fontWeight: "600",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: isRebalanceEnabled()
                      ? withAlpha(tokens.colors.buyBgColor, 0.20)
                      : tokens.colors.disabledBackground,
                    border: isRebalanceEnabled()
                      ? `1px solid ${tokens.colors.buyFontColor}`
                      : `1px solid ${tokens.colors.bodyBorderColor50}`,
                  },
                  "&:disabled": {
                    backgroundColor: tokens.colors.disabledBackground,
                    border: `1px solid ${tokens.colors.bodyBorderColor50}`,
                    color: tokens.colors.disabledFontColour,
                  },
                }}
              >
                {rebalanceApiLoading ? "Rebalancing..." : "Rebalance"}
              </Button>

              <Button
                variant="outlined"
                onClick={handlePortfolioPlay}
                disabled={!isPortfolioPlayEnabled()}
                sx={{
                  width: "130px",
                  px: 1,
                  height: "40px",
                  borderRadius: "8px",
                  backgroundColor: isPortfolioPlayEnabled()
                    ? `${tokens.palette.primaryA10}`
                    : tokens.colors.disabledBackground,
                  border: isPortfolioPlayEnabled()
                    ? `1px solid ${tokens.palette.primary}`
                    : `1px solid ${tokens.colors.bodyBorderColor50}`,
                  color: isPortfolioPlayEnabled()
                    ? tokens.palette.primary
                    : tokens.colors.disabledFontColour,
                  fontSize: "14px",
                  fontWeight: "600",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: isPortfolioPlayEnabled()
                      ? `${tokens.palette.primaryA25}`
                      : tokens.colors.disabledBackground,
                    border: isPortfolioPlayEnabled()
                      ? `1px solid ${tokens.palette.primary}`
                      : `1px solid ${tokens.colors.bodyBorderColor50}`,
                  },
                  "&:disabled": {
                    backgroundColor: tokens.colors.disabledBackground,
                    border: `1px solid ${tokens.colors.bodyBorderColor50}`,
                    color: tokens.colors.disabledFontColour,
                  },
                }}
              >
                Portfolio Play
              </Button>
            </Box>
          </Box>
        </Box>

        {/* {rebalanceApiError && (
          <Typography 
            variant="caption" 
            sx={{ 
              color: "error.main", 
              display: "block", 
              textAlign: "right",f
              mt: 1,
              mr: 2 
            }}
          >
            {rebalanceApiError}
          </Typography>
        )} */}
      </Paper>
    </>
  );
};

export default PortfolioRebalanceHeader;
